(() => {
  if (window.__macPageSizeLoaded) return;
  window.__macPageSizeLoaded = true;
  if (!window.location.pathname.includes('/collections/')) return;

  const OPTIONS = [24, 48, 100];
  const DEFAULT_SIZE = 24;
  const FILTER_PARAMS = [
    'mac_team', 'mac_player', 'mac_set', 'mac_vtag', 'mac_vtitle',
    'mac_grade', 'mac_company', 'mac_ctype'
  ];
  const INTERNAL_PARAMS = ['mac_per_page', 'mac_view_page', 'mac_page'];
  const nativeFetch = window.fetch.bind(window);

  let capturedSource = '';
  let capturedTotal = null;
  let capturedParsePromise = Promise.resolve();
  let activeController = null;
  let observer = null;
  let suppressObserver = false;
  let refreshTimer = null;

  const toUrl = (input) => {
    try {
      const raw = input instanceof Request ? input.url : String(input);
      return new URL(raw, window.location.href);
    } catch (_) {
      return null;
    }
  };

  const readTotalFromDoc = (doc) => {
    const marker = doc.querySelector('[data-mac-total-items]');
    const total = Number.parseInt(marker?.dataset.macTotalItems || '', 10);
    return Number.isFinite(total) && total >= 0 ? total : null;
  };

  const isOwnRequest = (input, init) => {
    try {
      const sourceHeaders = init?.headers || (input instanceof Request ? input.headers : undefined);
      const headers = new Headers(sourceHeaders || {});
      return headers.get('X-MAC-Page-Size') === '1';
    } catch (_) {
      return false;
    }
  };

  window.fetch = (input, init) => {
    const url = toUrl(input);
    const isFilterRequest = Boolean(
      url &&
      !isOwnRequest(input, init) &&
      url.origin === window.location.origin &&
      url.pathname === '/search' &&
      url.searchParams.get('view') === 'mac-filter'
    );

    if (isFilterRequest) {
      const source = new URL(url.toString());
      source.searchParams.delete('page');
      capturedSource = source.toString();
      capturedParsePromise = Promise.resolve();
    }

    const responsePromise = nativeFetch(input, init);

    if (isFilterRequest) {
      capturedParsePromise = responsePromise
        .then((response) => {
          if (!response.ok) return null;
          return response.clone().text();
        })
        .then((html) => {
          if (!html) return;
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const total = readTotalFromDoc(doc);
          if (total !== null) capturedTotal = total;
        })
        .catch(() => {});
    }

    return responsePromise;
  };

  const currentUrl = () => new URL(window.location.href);

  const hasCustomFilters = () => {
    const params = currentUrl().searchParams;
    return FILTER_PARAMS.some((param) => params.has(param));
  };

  const getPerPage = () => {
    const value = Number.parseInt(currentUrl().searchParams.get('mac_per_page') || '', 10);
    return OPTIONS.includes(value) ? value : DEFAULT_SIZE;
  };

  const getViewPage = () => {
    const value = Number.parseInt(currentUrl().searchParams.get('mac_view_page') || '', 10);
    return Number.isFinite(value) && value > 0 ? value : 1;
  };

  const getResults = () => document.getElementById('ResultsList');
  const getGrid = () => getResults()?.querySelector('.product-grid') || null;

  const getCurrentTotal = () => {
    if (hasCustomFilters() && capturedTotal !== null) return capturedTotal;
    const facetCount = Number.parseInt(
      document.querySelector('facets-form-component[data-results-count]')?.dataset.resultsCount || '',
      10
    );
    if (Number.isFinite(facetCount) && facetCount >= 0) return facetCount;
    const total = readTotalFromDoc(document);
    if (total !== null) return total;
    return getGrid()?.querySelectorAll(':scope > li.product-grid__item').length || 0;
  };

  const cleanCollectionSource = () => {
    const url = currentUrl();
    url.searchParams.delete('page');
    INTERNAL_PARAMS.forEach((param) => url.searchParams.delete(param));
    FILTER_PARAMS.forEach((param) => url.searchParams.delete(param));
    return url;
  };

  const getSourceUrl = () => {
    if (hasCustomFilters() && capturedSource) {
      const url = new URL(capturedSource, window.location.origin);
      url.searchParams.delete('page');
      return url;
    }
    return cleanCollectionSource();
  };

  const getBasePageSize = () => DEFAULT_SIZE;

  const disableInfiniteScroll = () => {
    document.querySelectorAll('results-list').forEach((list) => {
      list.setAttribute('infinite-scroll', 'false');
    });
    document
      .querySelectorAll('#ResultsList [ref="viewMorePrevious"], #ResultsList [ref="viewMoreNext"]')
      .forEach((node) => node.remove());
  };

  const setUrlState = (perPage, page, mode = 'push') => {
    const url = currentUrl();
    url.searchParams.set('mac_per_page', String(perPage));
    if (page > 1) url.searchParams.set('mac_view_page', String(page));
    else url.searchParams.delete('mac_view_page');
    url.searchParams.delete('page');
    url.searchParams.delete('mac_page');
    const method = mode === 'replace' ? 'replaceState' : 'pushState';
    window.history[method]({ macPageSize: true }, '', url.toString());
  };

  const pageTokens = (current, totalPages) => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, index) => index + 1);
    const values = new Set([1, totalPages, current - 1, current, current + 1]);
    const pages = [...values].filter((page) => page >= 1 && page <= totalPages).sort((a, b) => a - b);
    const tokens = [];
    let previous = 0;
    pages.forEach((page) => {
      if (previous && page - previous > 1) tokens.push('ellipsis');
      tokens.push(page);
      previous = page;
    });
    return tokens;
  };

  const renderFooter = () => {
    const results = getResults();
    if (!results) return;
    const inner = results.querySelector(':scope > div');
    if (!inner) return;

    results.classList.add('mac-page-size-active');
    disableInfiniteScroll();

    let footer = inner.querySelector(':scope > .mac-page-footer');
    if (!footer) {
      footer = document.createElement('div');
      footer.className = 'mac-page-footer';
      footer.innerHTML = `
        <div class="mac-page-footer__spacer" aria-hidden="true"></div>
        <nav class="mac-page-pagination" aria-label="Collection pages"></nav>
        <label class="mac-page-size-control">
          <span>Cards per page</span>
          <select data-mac-page-size aria-label="Cards per page">
            <option value="24">24</option>
            <option value="48">48</option>
            <option value="100">100</option>
          </select>
        </label>`;
      inner.appendChild(footer);

      footer.querySelector('[data-mac-page-size]')?.addEventListener('change', (event) => {
        const perPage = Number.parseInt(event.target.value, 10);
        if (!OPTIONS.includes(perPage)) return;
        setUrlState(perPage, 1, 'push');
        loadVirtualPage(1, perPage, { scroll: false });
      });

      footer.querySelector('.mac-page-pagination')?.addEventListener('click', (event) => {
        const button = event.target.closest('button[data-mac-view-page]');
        if (!button || button.disabled) return;
        const page = Number.parseInt(button.dataset.macViewPage || '', 10);
        if (!Number.isFinite(page) || page < 1) return;
        const perPage = getPerPage();
        setUrlState(perPage, page, 'push');
        loadVirtualPage(page, perPage, { scroll: true });
      });
    }

    const perPage = getPerPage();
    const total = getCurrentTotal();
    const totalPages = Math.max(1, Math.ceil(total / perPage));
    const current = Math.min(getViewPage(), totalPages);
    const select = footer.querySelector('[data-mac-page-size]');
    if (select) select.value = String(perPage);

    const nav = footer.querySelector('.mac-page-pagination');
    if (!nav) return;
    if (totalPages <= 1) {
      nav.innerHTML = '';
      nav.setAttribute('aria-hidden', 'true');
      return;
    }

    nav.removeAttribute('aria-hidden');
    const tokens = pageTokens(current, totalPages);
    const parts = [
      `<button type="button" class="mac-page-pagination__button mac-page-pagination__button--arrow" data-mac-view-page="${Math.max(1, current - 1)}" aria-label="Previous page" ${current === 1 ? 'disabled' : ''}>&lsaquo;</button>`
    ];
    tokens.forEach((token) => {
      if (token === 'ellipsis') {
        parts.push('<span class="mac-page-pagination__ellipsis" aria-hidden="true">&hellip;</span>');
      } else {
        const active = token === current;
        parts.push(`<button type="button" class="mac-page-pagination__button${active ? ' is-active' : ''}" data-mac-view-page="${token}" ${active ? 'aria-current="page"' : ''}>${token}</button>`);
      }
    });
    parts.push(`<button type="button" class="mac-page-pagination__button mac-page-pagination__button--arrow" data-mac-view-page="${Math.min(totalPages, current + 1)}" aria-label="Next page" ${current === totalPages ? 'disabled' : ''}>&rsaquo;</button>`);
    nav.innerHTML = parts.join('');
  };

  const fetchBasePage = async (source, page, signal) => {
    const url = new URL(source.toString());
    url.searchParams.set('page', String(page));
    const response = await nativeFetch(url.toString(), {
      signal,
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        'X-MAC-Page-Size': '1'
      }
    });
    if (!response.ok) throw new Error(`Page request failed (${response.status}).`);
    const html = await response.text();
    return new DOMParser().parseFromString(html, 'text/html');
  };

  const loadVirtualPage = async (requestedPage = getViewPage(), perPage = getPerPage(), options = {}) => {
    const results = getResults();
    const currentGrid = getGrid();
    if (!results || !currentGrid) {
      renderFooter();
      return;
    }

    const total = getCurrentTotal();
    if (total <= 0) {
      renderFooter();
      return;
    }

    const totalPages = Math.max(1, Math.ceil(total / perPage));
    const page = Math.min(Math.max(1, requestedPage), totalPages);
    if (page !== requestedPage) setUrlState(perPage, page, 'replace');

    const basePageSize = getBasePageSize();
    const startIndex = (page - 1) * perPage;
    const endIndex = Math.min(startIndex + perPage, total);
    const firstBasePage = Math.floor(startIndex / basePageSize) + 1;
    const lastBasePage = Math.max(firstBasePage, Math.ceil(endIndex / basePageSize));
    const source = getSourceUrl();

    if (activeController) activeController.abort();
    activeController = new AbortController();
    const { signal } = activeController;
    results.classList.add('mac-page-size-loading');
    results.setAttribute('aria-busy', 'true');

    try {
      const requests = [];
      for (let pageNumber = firstBasePage; pageNumber <= lastBasePage; pageNumber += 1) {
        requests.push(fetchBasePage(source, pageNumber, signal));
      }
      const docs = await Promise.all(requests);

      let freshTotal = null;
      const cards = [];
      docs.forEach((doc) => {
        if (freshTotal === null) freshTotal = readTotalFromDoc(doc);
        const grid = doc.querySelector('#ResultsList .product-grid');
        if (!grid) return;
        grid.querySelectorAll(':scope > li.product-grid__item').forEach((card) => cards.push(card));
      });

      if (freshTotal !== null && hasCustomFilters()) capturedTotal = freshTotal;

      const offset = startIndex - (firstBasePage - 1) * basePageSize;
      const needed = endIndex - startIndex;
      const selectedCards = cards.slice(offset, offset + needed);
      if (!selectedCards.length && needed > 0) throw new Error('No cards were returned for this page.');

      suppressObserver = true;
      currentGrid.replaceChildren(...selectedCards.map((card) => document.importNode(card, true)));
      currentGrid.querySelectorAll(':scope > li.product-grid__item').forEach((card, index) => {
        card.classList.remove(...[...card.classList].filter((name) => name.startsWith('product-grid__item--')));
        card.classList.add(`product-grid__item--${index}`);
      });
      window.setTimeout(() => { suppressObserver = false; }, 80);

      renderFooter();
      if (options.scroll) results.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } catch (error) {
      if (error.name !== 'AbortError') console.error('[MAC cards per page]', error);
    } finally {
      if (!signal.aborted) {
        results.classList.remove('mac-page-size-loading');
        results.removeAttribute('aria-busy');
      }
    }
  };

  const handleExternalResultsUpdate = async () => {
    if (!hasCustomFilters()) {
      capturedSource = '';
      capturedTotal = null;
    } else {
      await capturedParsePromise;
    }

    disableInfiniteScroll();
    renderFooter();
    const perPage = getPerPage();
    const page = getViewPage();
    if (perPage !== DEFAULT_SIZE || page > 1) {
      loadVirtualPage(page, perPage, { scroll: false });
    }
  };

  const scheduleExternalRefresh = () => {
    window.clearTimeout(refreshTimer);
    refreshTimer = window.setTimeout(handleExternalResultsUpdate, 70);
  };

  const startObserver = () => {
    if (observer) observer.disconnect();
    observer = new MutationObserver((records) => {
      if (suppressObserver) return;
      const relevant = records.some((record) => {
        const target = record.target instanceof Element ? record.target : record.target.parentElement;
        if (target?.closest?.('.mac-page-footer')) return false;
        if (target?.matches?.('.product-grid')) return true;
        return [...record.addedNodes].some((node) => {
          if (!(node instanceof Element)) return false;
          return node.matches('#ResultsList, .product-grid') || Boolean(node.querySelector?.('#ResultsList, .product-grid'));
        });
      });
      if (relevant) scheduleExternalRefresh();
    });
    observer.observe(document.body, { childList: true, subtree: true });
  };

  const init = () => {
    disableInfiniteScroll();
    renderFooter();
    startObserver();

    if (!hasCustomFilters()) {
      const perPage = getPerPage();
      const page = getViewPage();
      if (perPage !== DEFAULT_SIZE || page > 1) loadVirtualPage(page, perPage, { scroll: false });
    }

    window.addEventListener('popstate', () => {
      renderFooter();
      loadVirtualPage(getViewPage(), getPerPage(), { scroll: false });
    });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
